import * as E from './Enums';

type PriceReference = typeof E.PriceReference[keyof typeof E.PriceReference];

/**
 * Format a monetary value as currency (dollars or percent)
 * Respects user's showCents preference (defaults to true if not provided)
 * @param value The numeric value to format
 * @param type 'dollar' or 'percent'
 * @param showCents Optional override for showCents preference (defaults to true)
 * @returns Formatted string (e.g., "$123.45" or "12.34%")
 */
export const formatCurrency = (value: number | undefined, type: 'dollar' | 'percent' = 'dollar', showCents?: boolean): string => {
    if (value === undefined || isNaN(value)) return '...';

    // Default to true if showCents not provided
    const finalShowCents = showCents !== undefined ? showCents : true;
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const formatted = finalShowCents ? absValue.toFixed(2) : absValue.toFixed(0);

    let result: string;
    if (type === 'percent') {
        result = `${formatted}%`;
    } else {
        result = `$${formatted}`;
    }
    
    // Add minus sign at the beginning if negative
    if (isNegative) {
        result = `-${result}`;
    }
    
    return result;
};

/**
 * Format a number as a percentage
 * Respects user's showCents preference
 * @param value The numeric value to format
 * @param showCents Optional override for showCents preference
 * @returns Formatted string (e.g., "12.34%")
 */
export const formatPercent = (value: number | undefined, showCents?: boolean): string => {
    return formatCurrency(value, 'percent', showCents);
};

/**
 * Format a number as a dollar amount
 * Respects user's showCents preference
 * @param value The numeric value to format
 * @param showCents Optional override for showCents preference
 * @returns Formatted string (e.g., "$123.45")
 */
export const formatDollar = (value: number | undefined, showCents?: boolean): string => {
    return formatCurrency(value, 'dollar', showCents);
};

/**
 * Format a gain/loss value with color indication
 * @param value The numeric gain/loss value
 * @param type 'dollar' or 'percent'
 * @returns Object with formatted text and color
 */
export const formatGainLoss = (value: number | undefined, type: 'dollar' | 'percent' = 'dollar'): { text: string; color: string } => {
    if (value === undefined || isNaN(value)) {
        return { text: '...', color: '#999' };
    }

    const formatted = formatCurrency(value, type);
    const color = value >= 0 ? '#65ff65ff' : '#ff4444';

    return { text: formatted, color };
};
